import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Sparkles, Command } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { TypeBadge } from "@/components/Common";
import api from "@/lib/api";

const TYPE_ROUTE = { sop: "sops", faq: "faqs", workflow: "faqs", business_rule: "faqs", troubleshooting: "faqs", video: "training", download: "downloads" };

export default function GlobalSearch({ open, onOpenChange, activeProgram }) {
  const [q, setQ] = useState("");
  const [allProjects, setAllProjects] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const run = useCallback(async (query) => {
    if (!query.trim()) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q: query, program: activeProgram, all_projects: allProjects } });
      setData(data);
    } catch { setData(null); }
    setLoading(false);
  }, [activeProgram, allProjects]);

  useEffect(() => {
    const t = setTimeout(() => run(q), 250);
    return () => clearTimeout(t);
  }, [q, run]);

  const go = (r) => { onOpenChange(false); navigate(`/app/${TYPE_ROUTE[r.type] || "sops"}`); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Search the knowledge base</DialogTitle>

        <div className="flex items-center gap-3 px-5 border-b border-border">
          {loading ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> : <Search className="w-5 h-5 text-primary" />}
          <input
            autoFocus data-testid="global-search-input"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search SOPs, videos, FAQs, transcripts…"
            className="flex-1 py-5 text-[15px] outline-none bg-transparent placeholder:text-muted-foreground"
          />
          <kbd className="text-[10px] font-mono bg-muted border border-border rounded px-1.5 py-0.5 text-muted-foreground">ESC</kbd>
        </div>

        <div className="flex items-center justify-between px-5 py-2.5 border-b border-border bg-secondary/30">
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            {data ? `${data.count || 0} results` : "Start typing…"}
          </span>
          <label className="flex items-center gap-2 text-xs text-foreground/80">
            All programs
            <Switch checked={allProjects} onCheckedChange={setAllProjects} data-testid="search-all-projects-toggle" />
          </label>
        </div>

        <div className="max-h-[55vh] overflow-y-auto kp-scroll p-3">
          {data?.best && (
            <button onClick={() => go(data.best)} className="w-full text-left mb-3 rounded-xl border border-primary/30 bg-secondary/50 p-4 hover:bg-secondary/70 transition group">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary font-semibold">Best answer</span>
                <TypeBadge type={data.best.type} />
              </div>
              <p className="font-display text-lg text-foreground leading-tight">{data.best.title}</p>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{data.best.snippet}</p>
            </button>
          )}
          {data && Object.entries(data.groups || {}).map(([type, items]) => (
            <div key={type} className="mb-3">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground px-3 mb-1.5">{type}</p>
              {items.map((r) => (
                <button key={r.id} onClick={() => go(r)}
                  className="w-full text-left rounded-lg px-3 py-2.5 hover:bg-secondary/40 transition flex items-start gap-3 group">
                  <TypeBadge type={r.type} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground truncate">{r.title}</span>
                    <span className="block text-xs text-muted-foreground line-clamp-1 mt-0.5">{r.snippet}</span>
                  </span>
                  <Command className="w-3 h-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 mt-1 shrink-0 transition-opacity" />
                </button>
              ))}
            </div>
          ))}
          {data && !data.best && (!data.groups || Object.keys(data.groups).length === 0) && (
            <div className="py-10 text-center">
              <p className="font-display text-lg text-foreground">Nothing found</p>
              <p className="text-sm text-muted-foreground mt-1">Try broader keywords, or toggle cross-program search above.</p>
            </div>
          )}
          {!data && !q && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <p className="font-display text-lg text-foreground mb-1">Search everything</p>
              <p>SOPs · FAQs · Workflows · Videos · Downloads · Business Rules</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
